import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { postsAPI } from '../api';
import { Calendar, ArrowRight, Search } from 'lucide-react';

const POSTS_PER_PAGE = 24;
const CATEGORIES = ['all', 'news', 'events', 'history', 'attractions', 'famous-people', 'food-drink', 'announcements'];

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [localQuery, setLocalQuery] = useState(query);
  const [activeCategory, setActiveCategory] = useState('all');

  const performSearch = useCallback(async (q, skip = 0, category = 'all') => {
    setLoading(true);
    try {
      const params = { q, limit: POSTS_PER_PAGE, skip };
      if (category !== 'all') params.category = category;
      const response = await postsAPI.search(q, POSTS_PER_PAGE, skip, category !== 'all' ? category : undefined);
      setResults(response.data.posts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLocalQuery(query);
    setPage(1);
    setActiveCategory('all');
    if (query) {
      performSearch(query, 0, 'all');
    }
  }, [query, performSearch]);

  useEffect(() => {
    if (query && (page > 1 || activeCategory !== 'all')) {
      performSearch(query, (page - 1) * POSTS_PER_PAGE, activeCategory);
    }
  }, [page, activeCategory, query, performSearch]);

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    setPage(1);
    if (query) performSearch(query, 0, cat);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (localQuery.trim().length >= 2) {
      setSearchParams({ q: localQuery.trim() });
    }
  };

  const totalPages = Math.ceil(total / POSTS_PER_PAGE);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={query ? `Search: ${query} | Paisley.org.uk` : 'Search | Paisley.org.uk'}
        description="Search through thousands of Paisley articles covering news, events, history, attractions, and famous people."
        keywords="Search Paisley, Paisley articles, find Paisley news"
      />
      <Header />
      
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" data-testid="search-page-title">
            Search <span className="text-cyan-400">Paisley</span>
          </h1>
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder="Search articles, events, history..."
              className="w-full px-6 py-4 pr-14 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent backdrop-blur-sm"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg transition-colors"
              data-testid="search-submit-btn"
            >
              <Search size={20} className="text-white" />
            </button>
          </form>
          {query && !loading && (
            <p className="text-gray-300 mt-4 text-sm" data-testid="search-results-count">
              {total.toLocaleString()} result{total !== 1 ? 's' : ''} for "{query}"
            </p>
          )}
          {query && (
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                    activeCategory === cat
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat === 'all' ? 'All Categories' : cat.replace('-', ' ')}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Searching...</p>
            </div>
          ) : !query ? (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Enter a search term to find articles</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No results found for "{query}"</p>
              <p className="text-gray-400 mt-2">Try different keywords or browse by category</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {results.map((post) => (
                  <Link 
                    key={post.id} 
                    to={`/news/${post.slug}`}
                    className="group"
                    data-testid={`search-result-${post.slug}`}
                  >
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-cyan-500 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="relative overflow-hidden h-40">
                        <img 
                          src={post.featured_image} 
                          alt={post.title} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.src = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg'; }}
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded-full font-medium capitalize">
                            {post.category}
                          </span>
                          <Calendar size={12} />
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                        <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-cyan-500 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-cyan-600 text-xs font-medium group-hover:text-cyan-700">
                          Read More
                          <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={12} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="search-pagination">
                  <button
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SearchPage;
